import { Link, NavLink } from "react-router-dom";
import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  ArrowUpRight,
  ChevronDown,
  Menu,
  X,
  Sparkles,
  GraduationCap,
  BriefcaseBusiness,
  BookOpen,
  ExternalLink,
} from "lucide-react";

import {
  zulfahLogoAndText,
} from "../../../assets";

import Button from "../../button/button";

import {
  navCoursesDropDown,
  navLinkDatas,
  navResourceDropDown,
} from "../../../helpers/nav-link-data";

const portfolioDropdown = [
  {
    name: "Gasleet",
    url: "https://gasleet.com",
    description: "Technology powering last-mile LPG delivery.",
  },
  {
    name: "Farmeely",
    url: "https://farmeely.com",
    description: "Digital solutions for modern agriculture.",
  },
  {
    name: "Xwapit",
    url: "https://xwapit.com",
    description: "Building smarter digital experiences.",
  },
  {
    name: "Hisaab",
    url: "https://tryhisaab.com",
    description: "Simple tools for modern businesses.",
  },
];

const Navbar = () => {
  const [openMenu, setOpenMenu] = useState(false);
  const [openDropdown, setOpenDropdown] = useState(null);

  const toggleDropdown = (name) => {
    setOpenDropdown(openDropdown === name ? null : name);
  };

  const closeMenus = () => {
    setOpenDropdown(null);
    setOpenMenu(false);
  };

  return (
    <>
      {/* =====================================================
          DESKTOP / MAIN NAVBAR
      ====================================================== */}

      <motion.nav
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{
          duration: 0.7,
          ease: [0.22, 1, 0.36, 1],
        }}
        className="sticky top-0 z-[100] px-3 pt-3 sm:px-5 lg:px-8"
      >
        <div className="mx-auto max-w-[1500px]">

          <div className="relative flex h-[74px] items-center justify-between rounded-2xl border border-black/[0.07] bg-white/85 px-4 shadow-[0_10px_40px_rgba(0,0,0,0.05)] backdrop-blur-2xl sm:px-6">

            {/* =================================================
                LOGO
            ================================================== */}

            <Link
              to="/"
              onClick={closeMenus}
              className="group flex items-center"
            >
              <img
                src={zulfahLogoAndText}
                alt="Zulfah Academy"
                className="h-11 w-auto transition duration-300 group-hover:scale-[1.02] sm:h-12"
              />
            </Link>


            {/* =================================================
                DESKTOP NAVIGATION
            ================================================== */}

            <div className="hidden xl:flex items-center gap-1">

              {navLinkDatas.map((nav) => {

                const hasDropdown =
                  nav.name === "Resources" ||
                  nav.name === "Courses";

                return (
                  <div
                    key={nav.name}
                    className="relative"
                  >

                    <button
                      onClick={() =>
                        hasDropdown
                          ? toggleDropdown(nav.name)
                          : closeMenus()
                      }
                      className="group relative flex items-center gap-1.5 rounded-full px-4 py-3 text-[13px] font-medium text-gray-600 transition duration-300 hover:bg-black/[0.04] hover:text-black"
                    >

                      <NavLink
                        to={nav.url}
                        end={nav.end}
                        className={({ isActive }) =>
                          `transition ${
                            isActive
                              ? "text-black"
                              : ""
                          }`
                        }
                      >
                        {nav.name}
                      </NavLink>

                      {hasDropdown && (
                        <ChevronDown
                          className={`h-3.5 w-3.5 transition duration-300 ${
                            openDropdown === nav.name
                              ? "rotate-180"
                              : ""
                          }`}
                        />
                      )}

                      {/* active indicator */}
                      <NavLink to={nav.url}>
                        {({ isActive }) =>
                          isActive && (
                            <motion.span
                              layoutId="academy-nav-indicator"
                              className="absolute bottom-1 left-1/2 h-1 w-1 -translate-x-1/2 rounded-full bg-[#CF385D]"
                            />
                          )
                        }
                      </NavLink>

                    </button>


                    {/* =================================================
                        RESOURCES DROPDOWN
                    ================================================== */}

                    <AnimatePresence>
                      {nav.name === "Resources" &&
                        openDropdown === "Resources" && (

                          <motion.div
                            initial={{
                              opacity: 0,
                              y: 10,
                              scale: 0.97,
                            }}
                            animate={{
                              opacity: 1,
                              y: 0,
                              scale: 1,
                            }}
                            exit={{
                              opacity: 0,
                              y: 10,
                              scale: 0.97,
                            }}
                            transition={{
                              duration: 0.2,
                            }}
                            className="absolute left-1/2 top-[58px] w-[330px] -translate-x-1/2 overflow-hidden rounded-2xl border border-black/10 bg-white p-2 shadow-[0_25px_70px_rgba(0,0,0,0.12)]"
                          >

                            <div className="mb-1 rounded-xl bg-[#F7F7F5] p-4">

                              <div className="flex items-center gap-3">

                                <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-black text-white">
                                  <BookOpen className="h-4 w-4" />
                                </div>

                                <div>
                                  <p className="text-sm font-semibold">
                                    Resources
                                  </p>

                                  <p className="text-[11px] text-gray-400">
                                    Learn beyond the classroom
                                  </p>
                                </div>

                              </div>

                            </div>

                            {navResourceDropDown.map((item) => (

                              <Link
                                key={item.name}
                                to={item.url}
                                onClick={closeMenus}
                                className="group flex items-center justify-between rounded-xl px-4 py-3 transition hover:bg-[#F7F7F5]"
                              >

                                <span className="text-sm text-gray-600 transition group-hover:text-black">
                                  {item.name}
                                </span>

                                <ArrowUpRight
                                  className="h-3.5 w-3.5 text-gray-300 transition group-hover:-translate-y-0.5 group-hover:translate-x-0.5 group-hover:text-[#CF385D]"
                                />

                              </Link>

                            ))}

                          </motion.div>

                        )}
                    </AnimatePresence>


                    {/* =================================================
                        COURSES DROPDOWN
                    ================================================== */}

                    <AnimatePresence>
                      {nav.name === "Courses" &&
                        openDropdown === "Courses" && (

                          <motion.div
                            initial={{
                              opacity: 0,
                              y: 10,
                              scale: 0.97,
                            }}
                            animate={{
                              opacity: 1,
                              y: 0,
                              scale: 1,
                            }}
                            exit={{
                              opacity: 0,
                              y: 10,
                              scale: 0.97,
                            }}
                            transition={{
                              duration: 0.2,
                            }}
                            className="absolute left-1/2 top-[58px] w-[360px] -translate-x-1/2 overflow-hidden rounded-2xl border border-black/10 bg-white p-2 shadow-[0_25px_70px_rgba(0,0,0,0.12)]"
                          >

                            <div className="mb-1 rounded-xl bg-black p-5 text-white">

                              <div className="flex items-start justify-between">

                                <div>

                                  <span className="text-[8px] uppercase tracking-[0.3em] text-white/40">
                                    Zulfah Academy
                                  </span>

                                  <h3 className="mt-2 font-[ubuntu] text-xl font-semibold tracking-[-0.03em]">
                                    Choose your path.
                                  </h3>

                                </div>

                                <div className="flex h-9 w-9 items-center justify-center rounded-full bg-[#CF385D]">
                                  <GraduationCap className="h-4 w-4" />
                                </div>

                              </div>

                            </div>

                            {navCoursesDropDown.map((item, index) => (

                              <Link
                                key={item.name}
                                to={item.url}
                                onClick={closeMenus}
                                className="group flex items-center gap-4 rounded-xl px-4 py-3.5 transition hover:bg-[#F7F7F5]"
                              >

                                <span className="text-[9px] text-gray-300">
                                  0{index + 1}
                                </span>

                                <span className="flex-1 text-sm text-gray-600 transition group-hover:text-black">
                                  {item.name}
                                </span>

                                <ArrowUpRight
                                  className="h-3.5 w-3.5 text-gray-300 transition group-hover:-translate-y-0.5 group-hover:translate-x-0.5 group-hover:text-[#CF385D]"
                                />

                              </Link>

                            ))}

                          </motion.div>

                        )}
                    </AnimatePresence>

                  </div>
                );
              })}


              {/* =================================================
                  PORTFOLIO
              ================================================== */}

              <div className="relative">

                <button
                  onClick={() => toggleDropdown("Portfolio")}
                  className="flex items-center gap-1.5 rounded-full px-4 py-3 text-[13px] font-medium text-gray-600 transition hover:bg-black/[0.04] hover:text-black"
                >
                  Portfolio

                  <ChevronDown
                    className={`h-3.5 w-3.5 transition ${
                      openDropdown === "Portfolio"
                        ? "rotate-180"
                        : ""
                    }`}
                  />
                </button>


                <AnimatePresence>

                  {openDropdown === "Portfolio" && (

                    <motion.div
                      initial={{
                        opacity: 0,
                        y: 10,
                        scale: 0.97,
                      }}
                      animate={{
                        opacity: 1,
                        y: 0,
                        scale: 1,
                      }}
                      exit={{
                        opacity: 0,
                        y: 10,
                        scale: 0.97,
                      }}
                      className="absolute right-0 top-[58px] w-[350px] overflow-hidden rounded-2xl border border-black/10 bg-white p-2 shadow-[0_25px_70px_rgba(0,0,0,0.12)]"
                    >

                      <div className="rounded-xl bg-[#F7F7F5] p-5">

                        <div className="flex items-center gap-3">

                          <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-[#CF385D] text-white">
                            <BriefcaseBusiness className="h-4 w-4" />
                          </div>

                          <div>
                            <p className="text-sm font-semibold">
                              The Zulfah ecosystem
                            </p>

                            <p className="text-[11px] text-gray-400">
                              Explore what we're building
                            </p>
                          </div>

                        </div>

                      </div>


                      {portfolioDropdown.map((item) => (

                        <a
                          key={item.name}
                          href={item.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={closeMenus}
                          className="group flex items-center gap-4 rounded-xl px-4 py-4 transition hover:bg-[#F7F7F5]"
                        >

                          <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-black/[0.04] text-xs font-semibold text-gray-400 transition group-hover:bg-black group-hover:text-white">
                            {item.name.charAt(0)}
                          </div>

                          <div className="flex-1">

                            <div className="flex items-center gap-2">

                              <span className="text-sm font-medium">
                                {item.name}
                              </span>

                              <ExternalLink className="h-3 w-3 text-gray-300" />

                            </div>

                            <p className="mt-1 text-[10px] leading-4 text-gray-400">
                              {item.description}
                            </p>

                          </div>

                          <ArrowUpRight
                            className="h-4 w-4 text-gray-300 transition group-hover:-translate-y-1 group-hover:translate-x-1 group-hover:text-[#CF385D]"
                          />

                        </a>

                      ))}

                    </motion.div>

                  )}

                </AnimatePresence>

              </div>

            </div>


            {/* =================================================
                DESKTOP CTA
            ================================================== */}

            <div className="hidden xl:flex items-center gap-3">

              <Link
                to="/get-started"
                onClick={closeMenus}
                className="group flex items-center gap-2 rounded-full bg-black px-5 py-3 text-[11px] font-medium uppercase tracking-[0.14em] text-white transition duration-300 hover:-translate-y-0.5 hover:bg-[#CF385D]"
              >

                Start learning

                <ArrowUpRight className="h-3.5 w-3.5 transition group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />

              </Link>

            </div>


            {/* =================================================
                MOBILE BUTTON
            ================================================== */}

            <button
              onClick={() => setOpenMenu(true)}
              className="flex h-11 w-11 items-center justify-center rounded-full bg-black text-white transition hover:bg-[#CF385D] xl:hidden"
              aria-label="Open menu"
            >
              <Menu className="h-5 w-5" />
            </button>

          </div>

        </div>
      </motion.nav>


      {/* =====================================================
          MOBILE MENU
      ====================================================== */}

      <AnimatePresence>

        {openMenu && (

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[200] bg-[#080808] text-white xl:hidden"
          >

            {/* Mobile header */}

            <div className="flex h-[80px] items-center justify-between border-b border-white/10 px-6">

              <Link
                to="/"
                onClick={closeMenus}
                className="rounded-lg bg-white px-3 py-1.5"
              >
                <img
                  src={zulfahLogoAndText}
                  alt="Zulfah Academy"
                  className="h-9 w-auto"
                />
              </Link>

              <button
                onClick={() => setOpenMenu(false)}
                className="flex h-11 w-11 items-center justify-center rounded-full border border-white/10 transition hover:bg-white hover:text-black"
                aria-label="Close menu"
              >
                <X className="h-5 w-5" />
              </button>

            </div>


            {/* Mobile content */}

            <div className="flex h-[calc(100vh-80px)] flex-col overflow-y-auto px-6 py-10">

              <div className="mb-10">

                <div className="flex items-center gap-2">

                  <Sparkles className="h-3.5 w-3.5 text-[#CF385D]" />

                  <span className="text-[9px] uppercase tracking-[0.35em] text-white/30">
                    Learn · Build · Become
                  </span>

                </div>

                <h2 className="mt-5 max-w-sm font-[ubuntu] text-4xl font-semibold leading-[0.9] tracking-[-0.05em]">
                  Your next chapter
                  <span className="text-white/25">
                    {" "}starts here.
                  </span>
                </h2>

              </div>


              {/* Main mobile links */}

              <div className="border-t border-white/10">

                {navLinkDatas.map((nav, index) => (

                  <NavLink
                    key={nav.name}
                    to={nav.url}
                    onClick={closeMenus}
                    className="group flex items-center justify-between border-b border-white/10 py-5"
                  >

                    <div className="flex items-center gap-4">

                      <span className="text-[9px] text-white/20">
                        0{index + 1}
                      </span>

                      <span className="font-[ubuntu] text-2xl tracking-[-0.03em]">
                        {nav.name}
                      </span>

                    </div>

                    <ArrowUpRight className="h-4 w-4 text-white/20 transition group-hover:-translate-y-1 group-hover:translate-x-1 group-hover:text-[#CF385D]" />

                  </NavLink>

                ))}


                {/* Portfolio */}

                <div className="border-b border-white/10">

                  <button
                    onClick={() => toggleDropdown("MobilePortfolio")}
                    className="flex w-full items-center justify-between py-5 text-left"
                  >

                    <div className="flex items-center gap-4">

                      <span className="text-[9px] text-white/20">
                        05
                      </span>

                      <span className="font-[ubuntu] text-2xl tracking-[-0.03em]">
                        Portfolio
                      </span>

                    </div>

                    <ChevronDown
                      className={`h-5 w-5 text-white/30 transition ${
                        openDropdown === "MobilePortfolio"
                          ? "rotate-180"
                          : ""
                      }`}
                    />

                  </button>


                  <AnimatePresence>

                    {openDropdown === "MobilePortfolio" && (

                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        className="overflow-hidden"
                      >

                        <div className="pb-5">

                          {portfolioDropdown.map((item) => (

                            <a
                              key={item.name}
                              href={item.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              onClick={closeMenus}
                              className="flex items-center justify-between border-t border-white/5 py-4 pl-8"
                            >

                              <div>

                                <p className="text-sm">
                                  {item.name}
                                </p>

                                <p className="mt-1 text-[10px] text-white/30">
                                  {item.description}
                                </p>

                              </div>

                              <ExternalLink className="h-3.5 w-3.5 text-white/20" />

                            </a>

                          ))}

                        </div>

                      </motion.div>

                    )}

                  </AnimatePresence>

                </div>

              </div>


              {/* Bottom CTA */}

              <div className="mt-auto pt-10">

                <Link
                  to="/get-started"
                  onClick={closeMenus}
                  className="group flex w-full items-center justify-between rounded-2xl bg-[#CF385D] px-6 py-5"
                >

                  <div>

                    <span className="text-[9px] uppercase tracking-[0.25em] text-white/50">
                      Ready?
                    </span>

                    <p className="mt-1 font-[ubuntu] text-xl">
                      Start learning
                    </p>

                  </div>

                  <div className="flex h-10 w-10 items-center justify-center rounded-full bg-white text-black">

                    <ArrowUpRight className="h-4 w-4 transition group-hover:translate-x-1 group-hover:-translate-y-1" />

                  </div>

                </Link>

                <p className="mt-5 text-center text-[8px] uppercase tracking-[0.3em] text-white/20">
                  Zulfah Academy · Technology · Education · Impact
                </p>

              </div>

            </div>

          </motion.div>

        )}

      </AnimatePresence>
    </>
  );
};

export default Navbar;